
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ProductPageContainer from '@/components/product/ProductPageContainer';
import ProductLoadingState from '@/components/product/ProductLoadingState';
import ProductNotFound from '@/components/product/ProductNotFound';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Shop } from '@/lib/supabase/shops/types';
import { getShops } from '@/lib/supabase/shops';
import ShopHeader from '@/components/shop/ShopHeader';
import ShopFooter from '@/components/shop/ShopFooter';
import { useProductFetch } from '@/components/product/hooks/useProductFetch/useProductFetchHook';
import { useProductShopData } from '@/components/product/hooks/useProductShopData';

const ProductPage = () => {
  const { shopSlug, productSlug } = useParams<{ shopSlug: string; productSlug: string }>();
  const [shop, setShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);

  // Using our hook for dynamic document title
  useDocumentTitle(shop ? `${shop.name} - Produit` : 'Produit | Scaloo');

  // Get shop and organization ID
  const { shopId, organizationId, loading: shopLoading } = useProductShopData(shopSlug);
  
  // Fetch product data
  const { product, loading: productLoading } = useProductFetch(
    productSlug,
    shopId,
    organizationId
  );

  useEffect(() => {
    const fetchShopData = async () => {
      if (!shopSlug) return;
      
      try {
        setLoading(true);
        // Fetch all shops and find the one matching the slug
        const allShops = await getShops();
        const foundShop = allShops.find(s => 
          (s.slug && s.slug.toLowerCase() === shopSlug.toLowerCase()) || 
          (s.name && s.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') === shopSlug.toLowerCase())
        );
        
        if (foundShop) {
          setShop(foundShop);
        }
      } catch (error) {
        console.error('Error fetching shop data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchShopData();
  }, [shopSlug]);

  // Show loading state if any data is still loading
  if (loading || shopLoading || productLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <ShopHeader shop={shop} />
        <div className="pt-16 flex-grow">
          <ProductLoadingState />
        </div>
        <ShopFooter shop={shop} shopSlug={shopSlug} />
      </div>
    );
  }

  // Show 404 if product not found
  if (!product) {
    return (
      <div className="flex flex-col min-h-screen">
        <ShopHeader shop={shop} />
        <div className="pt-16 flex-grow">
          <ProductNotFound shopSlug={shopSlug} />
        </div>
        <ShopFooter shop={shop} shopSlug={shopSlug} />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
      <ShopHeader shop={shop} />
      <div className="pt-16 flex-grow">
        <ProductPageContainer 
          product={product}
          shopId={shopId}
          organizationId={organizationId}
        />
      </div>
      <ShopFooter shop={shop} shopSlug={shopSlug} />
    </div>
  );
};

export default ProductPage;
